import clsx from "clsx";
import __ from "lodash";
import { useContext, useEffect, useRef, useState } from "react";
import PropTypes from 'prop-types';
import { ReactReduxContext } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { RouteName } from "../../../../constants/Constants";
import { Localize as AriaLocalize } from "../../../../shared/ariaLabelLocalize";
import IconTooltip, { ICON_TOOLTIP_HOST } from "../../../UI/Tooltip/IconTooltip";


const SessionSearch = (props) => {
    const {isDiscardChanged, searchTextChangeHandler, localisedValues, onActiveChange} = props;

    // This component receives `localisedValues` but not `language`, so read the
    // locale off the store the way AssigneeDropDown does. The close label lives
    // in ariaLabelLocalize because it is cross-cutting a11y vocabulary, and it
    // must be a single translated phrase — composing "close" + "search" gives
    // broken grammar in most of the 17 locales.
    const reduxContext = useContext(ReactReduxContext);
    const ariaLangId = reduxContext?.store?.getState()?.language?.selectedlanguage?.id;
    const ariaLocalised = AriaLocalize[ariaLangId] || AriaLocalize['en_US'];

    const [isActive, setIsActive] = useState(false);
    const [searchText, setSearchText] = useState('');

    const params = useParams();
    const history = useHistory();

    const isMount = useRef(false);

    useEffect(() => {
        if(!isMount.current){
            isMount.current = true;
            return;
        }
        const timeOutId = setTimeout(() => {
          const updatedSearchText = searchText.trim()
            if(updatedSearchText.length >= 0){
                if (!__.isEmpty(params) && !isDiscardChanged) {
                    history.push(RouteName.SESSIONS);
                }
                searchTextChangeHandler(updatedSearchText);
            }
        }, 500);
        return () => clearTimeout(timeOutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText]);

    useEffect(() => {
        document.addEventListener('keyup', (e) => {
            if (e.key === "Escape" || e.code == 27 ) { // escape key maps to keycode `27`
                setIsActive(false);
                setSearchText('')
            }
        })
    }, [])

    // Notify the parent so it can mark the sort/filter region `inert` while the
    // reveal is open — keeps the reveal's absolute-positioned overlay from
    // covering focus rings on sort/filter buttons for keyboard users.
    useEffect(() => {
        if (onActiveChange) onActiveChange(isActive);
    }, [isActive, onActiveChange]);

    const toggleClass = () => {
        if (!isActive) document.getElementById('session-search-autoFocus').focus()
        setIsActive(!isActive);
        if(isActive){
            setSearchText('');
        }
    }

    const searchButtonLabel = isActive ? ariaLocalised['close_search'] : (localisedValues['search'] || 'Search');

    return (
        <div className={clsx("session-search-box clearfix", isActive && "active")}>
            {/* KBD-D01 (A11Y-14): the reveal is `opacity: 0` when inactive but
                the input stays in the DOM — that made Tab land on an invisible
                input and the tester saw "focus disappears". Take the input out
                of the tab order and hide it from AT while the reveal is closed. */}
            <div className="session-search-form" aria-hidden={!isActive}>
                <div className="form-group">
                    <input
                        type="text"
                        id="session-search-autoFocus"
                        value={searchText}
                        className="form-control focus-font-bold"
                        placeholder={localisedValues["search"]}
                        onChange={event => setSearchText(event.target.value)}
                        tabIndex={isActive ? 0 : -1}
                    />
                    <span className="search-form-icon icon-search" aria-hidden="true"></span>
                </div>
            </div>
            <div className="session-search-btn-row">
                <button
                    className={`btn session-search-btn ${ICON_TOOLTIP_HOST}`}
                    onClick={toggleClass}
                    aria-label={searchButtonLabel}
                >
                    <span className="btn-icon btn-search-icon icon-search" aria-hidden="true"></span>
                    <span className="btn-icon icon-close-thin" aria-hidden="true"></span>
                    <IconTooltip label={searchButtonLabel} hideWhenExpanded={false} />
                </button>
            </div>
        </div>
    );
}

SessionSearch.propTypes = {
  isDiscardChanged: PropTypes.bool,
  searchTextChangeHandler: PropTypes.func,
  localisedValues: PropTypes.object,
  onActiveChange: PropTypes.func,
};

export default SessionSearch;
